/**
* Locale-aware product page generator. Uses a per-(locale, slug) PRNG
* so every one of the 32 products × 6 locales = 192 mirror pages emits
* a unique but deterministic combination of lead/H2/paragraphs/FAQ.
* Content is authored per locale (see locale-product-copy.ts), not
* machine-translated.
*/
import type { Product } from "@/data/products";
import { seedFor } from "@/data/products/copy-pool";
import { PRODUCT_LOCALE_COPY, type ProductLocaleCopy } from "./locale-product-copy";
import type { StoreLocale } from "./locale-copy";
import { SAFE_PRODUCT_COPY } from "./locale-product-safe";
import { quotePhrase } from "@/config/pricing";

export interface LocaleProductContent {
  copy: ProductLocaleCopy;
  h1: string;
  lead: string;
  sections: Array<{ heading: string; paragraphs: string[] }>;
  faqs: Array<{ q: string; a: string }>;
  howto: Array<{ tag: string; text: string }>;
  metaTitle: string;
  metaDesc: string;
  wordCount: number;
}

function fill(t: string, vars: Record<string, string | number>) {
  return t.replace(/\{(\w+)\}/g, (_m, k) => String(vars[k] ?? ""));
}

function pickN<T>(rnd: () => number, arr: readonly T[], n: number): T[] {
  const pool = arr.slice();
  const out: T[] = [];
  const take = Math.min(n, pool.length);
  for (let i = 0; i < take; i++) {
    const j = Math.floor(rnd() * pool.length);
    out.push(pool.splice(j, 1)[0]);
  }
  return out;
}

export function generateLocaleProductContent(
  product: Product,
  locale: StoreLocale,
): LocaleProductContent {
  const copy = PRODUCT_LOCALE_COPY[locale];
  // Distinct PRNG per (locale, slug) — different combinations across locales.
  const rnd = seedFor(`${locale}:${product.slug}`);
  const name = product.doc.nameEn;
  const vars = {
    name,
    lo: quotePhrase(locale),
    hi: quotePhrase(locale),
  };

  void rnd;
  const safe = SAFE_PRODUCT_COPY[locale];
  const h1 = fill(safe.h1, vars);
  const lead = fill(safe.lead, vars);
  const sections = safe.sections.map((s) => ({
    heading: fill(s.heading, vars),
    paragraphs: s.paragraphs.map((p) => fill(p, vars)),
  }));
  const faqs = safe.faqs.map((f) => ({ q: fill(f.q, vars), a: fill(f.a, vars) }));
  const howto = safe.howto.map((s) => ({ tag: s.tag, text: fill(s.text, vars) }));
  const metaTitle = fill(copy.metaTitle, vars);
  const metaDesc = fill(copy.metaDesc, vars);

  const text = [
    lead,
    ...sections.flatMap((s) => [s.heading, ...s.paragraphs]),
    ...faqs.flatMap((f) => [f.q, f.a]),
    ...howto.map((s) => s.text),
  ].join(" ");
  const wordCount = text.split(/\s+/).filter(Boolean).length;

  const publicCopy = { ...copy, h1: "", leads: [], h2Pool: [], paragraphs: [], faqs: [], howto: [] };
  return { copy: publicCopy, h1, lead, sections, faqs, howto, metaTitle, metaDesc, wordCount };
}
